// Cloudinary image optimization helper.
//
// Admin uploads full-resolution images (often 2-4 MB). Serving those raw to
// every visitor is the single biggest cause of the slow LCP on theyellove.in.
// Cloudinary can transform images on the fly via URL params inserted right
// after `/upload/`:
//   f_auto  → best format the browser supports (WebP / AVIF)
//   q_auto  → automatic quality (visually lossless, far fewer bytes)
//   w_<n>   → cap the width so a phone never downloads a 4000px image
//   dpr_auto→ serve a sharper image only on high-density screens
//
// Usage:
//   <img src={cldImg(banner.image_url, 1200)} />
//   <img src={cldImg(product.image, 400)} />
//
// Non-Cloudinary URLs (or empty values) are returned untouched, so this is
// safe to wrap around any image source.

const CLOUDINARY_UPLOAD_RE = /\/image\/upload\//;

/**
 * Insert optimization transformations into a Cloudinary delivery URL.
 *
 * @param {string} url   The original image URL.
 * @param {number} [width] Max width in px. Omit to keep original dimensions.
 * @returns {string} An optimized URL, or the input unchanged if not Cloudinary.
 */
export function cldImg(url, width) {
  if (!url || typeof url !== 'string') return url;
  if (!CLOUDINARY_UPLOAD_RE.test(url)) return url;

  // Don't double-transform if a transformation is already present.
  // (A transformation segment sits between `/upload/` and the version `v123/`
  //  or the public id, and contains comma/underscore params like `f_auto`.)
  const afterUpload = url.split('/image/upload/')[1] || '';
  const firstSeg = afterUpload.split('/')[0];
  const alreadyTransformed = /(^|,)(f_|q_|w_|h_|c_|dpr_|e_|g_)/.test(firstSeg);
  if (alreadyTransformed) return url;

  const params = ['f_auto', 'q_auto', 'dpr_auto'];
  if (width) params.push(`w_${width}`, 'c_limit');

  return url.replace(CLOUDINARY_UPLOAD_RE, `/image/upload/${params.join(',')}/`);
}

export default cldImg;
